import { useEffect, useState } from 'react'
import type { ELK, ElkNode } from 'elkjs/lib/elk-api'
import type { GraphPoint } from '@/frontend/features/Fde/useOntologyGraphLayout'
import type { SchemaEdgeRecord, SchemaNodeRecord } from '@/frontend/features/Fde/ontologySchemaProjection'

const NODE_WIDTH = 240
const NODE_HEIGHT = 96
const OVERVIEW_POSITIONS: Record<SchemaNodeRecord['group'], GraphPoint> = {
  actors: { x: 0, y: 20 },
  governance: { x: 0, y: 180 },
  work: { x: 360, y: 100 },
  technology: { x: 720, y: 20 },
  information: { x: 720, y: 180 },
  outcomes: { x: 1080, y: 100 },
  other: { x: 1080, y: 280 },
}
let elkPromise: Promise<ELK> | null = null

function loadElk() {
  elkPromise ||= import('elkjs/lib/elk.bundled.js').then(({ default: ELK }) => new ELK())
  return elkPromise
}

interface State {
  nodes: Array<{ record: SchemaNodeRecord; position: GraphPoint }>
  edges: Array<{ record: SchemaEdgeRecord; points: GraphPoint[] }>
  loading: boolean
  revision: number
}

interface NodeRect extends GraphPoint { width: number; height: number }

function segmentHitsNode(from: GraphPoint, to: GraphPoint, node: NodeRect) {
  const left = node.x - 12
  const right = node.x + node.width + 12
  const top = node.y - 12
  const bottom = node.y + node.height + 12
  if (from.x === to.x) return from.x > left && from.x < right && Math.max(from.y, to.y) > top && Math.min(from.y, to.y) < bottom
  if (from.y === to.y) return from.y > top && from.y < bottom && Math.max(from.x, to.x) > left && Math.min(from.x, to.x) < right
  return false
}

function routeScore(points: GraphPoint[], obstacles: NodeRect[]) {
  const collisions = points.slice(1).reduce((total, point, index) => (
    total + obstacles.filter((node) => segmentHitsNode(points[index], point, node)).length
  ), 0)
  const length = points.slice(1).reduce((total, point, index) => total + Math.abs(point.x - points[index].x) + Math.abs(point.y - points[index].y), 0)
  return collisions * 1_000_000 + length + points.length * 12
}

function overviewRoute(source: GraphPoint, target: GraphPoint, obstacles: NodeRect[]) {
  const sourceCenter = { x: source.x + NODE_WIDTH / 2, y: source.y + NODE_HEIGHT / 2 }
  const targetCenter = { x: target.x + NODE_WIDTH / 2, y: target.y + NODE_HEIGHT / 2 }
  const horizontal = Math.abs(targetCenter.x - sourceCenter.x) >= Math.abs(targetCenter.y - sourceCenter.y)
  let start: GraphPoint
  let end: GraphPoint

  if (horizontal) {
    const rightward = targetCenter.x >= sourceCenter.x
    start = { x: source.x + (rightward ? NODE_WIDTH : 0), y: sourceCenter.y }
    end = { x: target.x + (rightward ? 0 : NODE_WIDTH), y: targetCenter.y }
  } else {
    const downward = targetCenter.y >= sourceCenter.y
    start = { x: sourceCenter.x, y: source.y + (downward ? NODE_HEIGHT : 0) }
    end = { x: targetCenter.x, y: target.y + (downward ? 0 : NODE_HEIGHT) }
  }

  const minX = Math.min(start.x, end.x, ...obstacles.map((node) => node.x)) - 36
  const maxX = Math.max(start.x, end.x, ...obstacles.map((node) => node.x + node.width)) + 36
  const minY = Math.min(start.y, end.y, ...obstacles.map((node) => node.y)) - 36
  const maxY = Math.max(start.y, end.y, ...obstacles.map((node) => node.y + node.height)) + 36
  const candidates = horizontal
    ? [
        [start, { x: (start.x + end.x) / 2, y: start.y }, { x: (start.x + end.x) / 2, y: end.y }, end],
        [start, { x: start.x, y: minY }, { x: end.x, y: minY }, end],
        [start, { x: start.x, y: maxY }, { x: end.x, y: maxY }, end],
      ]
    : [
        [start, { x: start.x, y: (start.y + end.y) / 2 }, { x: end.x, y: (start.y + end.y) / 2 }, end],
        [start, { x: minX, y: start.y }, { x: minX, y: end.y }, end],
        [start, { x: maxX, y: start.y }, { x: maxX, y: end.y }, end],
      ]
  return candidates.sort((left, right) => routeScore(left, obstacles) - routeScore(right, obstacles))[0]
}

function overviewLayout(nodes: SchemaNodeRecord[], edges: SchemaEdgeRecord[]) {
  const groupOffsets = new Map<SchemaNodeRecord['group'], number>()
  const positioned = nodes.map((record) => {
    const offset = groupOffsets.get(record.group) || 0
    groupOffsets.set(record.group, offset + 1)
    const anchor = OVERVIEW_POSITIONS[record.group]
    return { record, position: { x: anchor.x, y: anchor.y + offset * 124 } }
  })
  const positions = new Map(positioned.map((node) => [node.record.id, node.position]))
  const routed = edges.map((record) => {
    const source = positions.get(record.source)
    const target = positions.get(record.target)
    if (!source || !target) return { record, points: [] }
    const obstacles = positioned
      .filter((node) => node.record.id !== record.source && node.record.id !== record.target)
      .map((node) => ({ ...node.position, width: NODE_WIDTH, height: NODE_HEIGHT }))
    return { record, points: overviewRoute(source, target, obstacles) }
  })
  return { nodes: positioned, edges: routed }
}

export default function useOntologySchemaLayout(nodes: SchemaNodeRecord[], edges: SchemaEdgeRecord[], focusId: string | null) {
  const [state, setState] = useState<State>({ nodes: [], edges: [], loading: true, revision: 0 })

  useEffect(() => {
    let cancelled = false
    const familyOverview = !focusId && nodes.length > 0 && nodes.every((node) => node.kind === 'family') && edges.every((edge) => edge.kind === 'summary')
    if (familyOverview) {
      const layout = overviewLayout(nodes, edges)
      setState((current) => ({ ...layout, loading: false, revision: current.revision + 1 }))
      return () => { cancelled = true }
    }
    setState((current) => ({ ...current, loading: true }))
    const graph: ElkNode = {
      id: 'semantic-model',
      layoutOptions: {
        'elk.algorithm': 'layered', 'elk.direction': 'RIGHT', 'elk.edgeRouting': 'ORTHOGONAL',
        'elk.spacing.nodeNode': '64', 'elk.spacing.componentComponent': '92',
        'elk.layered.spacing.nodeNodeBetweenLayers': focusId ? '120' : '132',
        'elk.layered.spacing.edgeNodeBetweenLayers': '38', 'elk.layered.spacing.edgeEdgeBetweenLayers': '22',
        'elk.layered.layering.strategy': focusId ? 'COFFMAN_GRAHAM' : 'NETWORK_SIMPLEX',
        'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
        'elk.layered.crossingMinimization.greedySwitch.type': 'TWO_SIDED',
        'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX', 'elk.layered.nodePlacement.favorStraightEdges': 'true',
        'elk.layered.cycleBreaking.strategy': 'GREEDY', 'elk.layered.thoroughness': '30',
      },
      children: nodes.map((node) => ({
        id: node.id, width: NODE_WIDTH, height: NODE_HEIGHT,
        layoutOptions: node.id === focusId ? { 'org.eclipse.elk.layered.layering.layerConstraint': 'FIRST_SEPARATE' } : undefined,
      })),
      edges: edges.map((edge) => ({ id: edge.id, sources: [edge.source], targets: [edge.target] })),
    }
    loadElk().then((elk) => elk.layout(graph)).then((result) => {
      if (cancelled) return
      const byId = new Map(nodes.map((node) => [node.id, node]))
      const positioned = (result.children || []).flatMap((node) => {
        const record = byId.get(node.id)
        return record ? [{ record, position: { x: node.x || 0, y: node.y || 0 } }] : []
      })
      const routes = new Map<string, GraphPoint[]>((result.edges || []).flatMap((edge) => {
        const section = edge.sections?.[0]
        return section ? [[edge.id, [section.startPoint, ...(section.bendPoints || []), section.endPoint].map((point) => ({ x: point.x, y: point.y }))] as [string, GraphPoint[]]] : []
      }))
      setState((current) => ({ nodes: positioned, edges: edges.map((record) => ({ record, points: [...(routes.get(record.id) || [])] })), loading: false, revision: current.revision + 1 }))
    }).catch(() => {
      if (cancelled) return
      setState((current) => ({
        nodes: nodes.map((record, index) => ({ record, position: { x: (index % 4) * 320, y: Math.floor(index / 4) * 156 } })),
        edges: edges.map((record) => ({ record, points: [] })), loading: false, revision: current.revision + 1,
      }))
    })
    return () => { cancelled = true }
  }, [edges, focusId, nodes])

  return state
}

export const ontologySchemaNodeSize = { width: NODE_WIDTH, height: NODE_HEIGHT }
